import {Button, Input, message, Progress, Upload, UploadFile, UploadProps} from "antd";
import {useEffect, useState} from "react";
import useUserContext from "../../../hooks/useUserContext";
import useToast from "../../../hooks/useToast";
import {InboxOutlined} from "@ant-design/icons";
import {getUploadUrl} from "../../../api/getUploadUrl";
import axios from "axios";
import {processFlashyCsv} from "../../../api/processFlashyCsv";
import {ToastSkin} from "../../../context/ToastContext";
import {setIntegrationKey} from "../../../api/setIntegrationKey";
import {ConsentIntegrationOptions} from "../ConsentIntegrationSection";
import {getIntegrationKey} from "../../../api/getIntegrationKey";

const { Dragger } = Upload;

interface FlashyIntegrationFormProps {
    onClose: () => void;
    isConnected: boolean;
    setIsConnected: (newVal: boolean) => void;
}

const FlashyIntegrationForm = ({ onClose, isConnected, setIsConnected }: FlashyIntegrationFormProps) => {
    const [apiKey, setApiKey] = useState('');
    const [initialApiKey, setInitialApiKey] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const { selectedDomain } = useUserContext();
    const { showToast } = useToast();
    const [progress, setProgress] = useState(0); // Track percentage
    const [uploading, setUploading] = useState(false);
    const [wasUploaded, setWasUploaded] = useState(false);
    const [totalUpdated, setTotalUpdated] = useState(0);

    const getInitialApiKey = async () => {
        if(selectedDomain) {
            const apiKeyResponse = await getIntegrationKey(selectedDomain, ConsentIntegrationOptions.FLASHY);
            setApiKey(apiKeyResponse.data || '');
            setInitialApiKey(apiKeyResponse.data || '');
        }
    }

    const onUploadCsv = async () => {
        if (fileList.length === 0) return;
        const formData = new FormData();
        formData.append('file', fileList[0] as any);
        setUploading(true);
        setProgress(0); // Reset progress
        try {
            if(selectedDomain) {
                const response = await getUploadUrl(selectedDomain);
                const signedUrl = response?.data?.url;
                const fileKey = response?.data?.fileKey;
                if(signedUrl && fileKey) {
                    const fileToUpload = fileList[0].originFileObj || fileList[0];
                    if (fileToUpload.size === 0) {
                        console.error("The file object itself is empty!");
                        return;
                    }
                    const response2 = await axios.put(signedUrl, fileToUpload, {
                        // transformRequest: [(data) => data],
                        onUploadProgress: (progressEvent) => {
                            const { loaded, total } = progressEvent;
                            if (total) {
                                let percent = Math.floor((loaded * 100) / total);
                                // We cap it at 90 to show there is still "Processing" to do on the server
                                setProgress(Math.round(percent * 0.9));
                            }
                        }
                    });
                    setProgress(85);
                    const response3 = await processFlashyCsv(selectedDomain, fileKey);
                    setProgress(100);
                    const totalAdded = response3?.data?.totalAdded?.toLocaleString();
                    setTotalUpdated(totalAdded);
                    if(parseInt(totalAdded) > 0) {
                        showToast({
                            skin: ToastSkin.approval,
                            text: `${totalAdded} customers were updated successfully!`
                        });
                    } else {
                        showToast({
                            skin: ToastSkin.error,
                            text: `${totalAdded} customers were updated`
                        });
                    }
                    setWasUploaded(true);
                    setFileList([]);
                }
            }
        } catch (err) {
            message.error('Upload failed. Please try again.');
        } finally {
            setUploading(false);
            // Optional: Delay hiding the bar so the user sees "100%"
            setTimeout(() => setProgress(0), 2000);
        }
    }

    const submitApiKey = async () => {
        if(selectedDomain && apiKey !== initialApiKey) {
            const response = await setIntegrationKey(selectedDomain, ConsentIntegrationOptions.FLASHY, apiKey);
            if(response.data === 'ok') {
                setInitialApiKey(apiKey);
                apiKey ? setIsConnected(true) : setIsConnected(false);
                showToast({text: "Flashy connection successful! Your API key has been updated.", skin: ToastSkin.approval});
            } else {
                showToast({text: "Connection to Flashy failed, please check your API Key and try again.", skin: ToastSkin.error});
            }
        }
    }

    const props: UploadProps = {
        name: 'file',
        multiple: true,
        onRemove: (file) => {
            setFileList([]);
        },
        beforeUpload: (file) => {
            setFileList([file]);
            return false; // Stop auto-upload
        },
        fileList,
        maxCount: 1, // Only one CSV at a time for Vuelve
        accept: '.csv', // Restrict to CSV files
    };

    useEffect(() => {
        getInitialApiKey();
    }, [selectedDomain]);

    return (
        <div className="flashy integration-form">
            <div className="left">
                <div className="title">1. Import Customers</div>
                <ul>
                    <li>Export your contacts as a CSV from Flashy.</li>
                    <li>
                        <div>Upload the file here (Max 20MB).</div>
                        {totalUpdated ?
                            <div className="total-updated">
                                <div>Total customers updated: {totalUpdated}</div>
                                <div className="text-button" onClick={() => setTotalUpdated(0)}>Add more customers</div>
                            </div>
                        :
                            <div className="integration-form">
                                <div className="dragger-container">
                                    <Dragger {...props} style={{fontFamily: 'quicksand-bold'}}>
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined/>
                                        </p>
                                        <p className="ant-upload-text">{uploading ? 'Uploading & Processing CSV...' : 'Drag Flashy CSV here to sync customers'}</p>
                                        <p className="ant-upload-hint">Limit: 20MB. Only .csv files are supported.</p>
                                    </Dragger>
                                    {progress > 0 && (
                                        <Progress
                                            percent={progress}
                                            status={progress === 100 ? "success" : "active"}
                                            style={{marginTop: 16}}
                                        />
                                    )}
                                </div>
                                <Button disabled={!fileList?.length} size="large" loading={isLoading} type="primary" onClick={onUploadCsv}>Upload
                                    Contacts List</Button>
                            </div>
                        }
                    </li>
                </ul>
            </div>
            <div className="right">
                <div className="title">2. Enable Real-time Sync</div>
                <ul>
                    <li>{`Copy your API key from Flashy Settings > API.`}</li>
                    <li>
                        <div>Paste it down below and submit</div>
                        <Input value={apiKey} onChange={(e: any) => setApiKey(e.target.value)} placeholder='Flashy API-Key' />
                        <Button size="large" loading={isLoading} type="primary" onClick={submitApiKey} disabled={initialApiKey === apiKey}>Connect Flashy</Button>
                    </li>
                </ul>
            </div>
        </div>
    );
}

export default FlashyIntegrationForm;